import React, { memo, useEffect, useMemo, useState } from 'react'
import { useProductLink } from 'src/sdk/product/useProductLink'
import { useBuyButton } from 'src/sdk/cart/useBuyButton'
import { addProductOnReducer } from 'src/components/sections/CollectionCarousel/Event/AddProducts'
import { useInView } from 'react-intersection-observer'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import useReleaseDate from 'src/hooks/useReleaseDate'
import { OrderImagesByRegex } from 'src/utils/OrderImagesByRegex'
import priceRangeConditions from 'src/components/product/ProductCard/utils/priceRangeConditions'

import type { ProductCardProps, Variant } from './ProductCardTypes'
import HandleModalSaveProductAndWishlist from './handleModalSaveProductAndWishlist'
import { TagsCollection, validateVivaraYou } from './tagsCollection'
import ProductCardReturn from './ProductCardReturn'
import { getSKUPriceRange } from './utils/getSKUPriceRange'

import './product-card.scss'

const shelfClassVariant = (variant?: string) => {
  return variant ? `shelf-product-card-${variant}` : 'shelf-product-card'
}

export const variantInStock = (variants: Variant[], isKit: boolean) => {
  return variants?.filter((variantUnit) => {
    const offersProduct = variantUnit?.offers?.offers?.[0]

    return isKit && offersProduct?.price > 0
      ? true
      : offersProduct?.availability === 'https://schema.org/InStock'
  })
}

export const newSpotPriceProduct = (
  isKit: boolean,
  newVariantsInStock: Variant[],
  newListPrice: number
) => {
  return isKit && newVariantsInStock?.[0]?.offers?.lowPrice === 0
    ? newListPrice
    : newVariantsInStock?.[0]?.offers?.lowPrice ?? 0
}

export const isProductAvailableInStock = (
  isKit: boolean,
  newVariantsInStock: Variant[],
  newPrice: number
) => {
  return isKit && newPrice > 0
    ? true
    : newVariantsInStock?.[0]?.offers?.offers?.[0].availability ===
        'https://schema.org/InStock' ?? false
}

function ProductCard({
  product,
  index,
  aspectRatio = 1,
  variant,
  dispatcher,
  productsDataToEvent,
  setPayloadEvent,
  wishlistStyle,
  border,
  isRelatedProducts,
}: ProductCardProps) {
  const { ref, inView } = useInView()
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false)
  const [collections, setCollections] = useState<CollectionsType>()
  const [allCollections, setAllCollections] = useState<CollectionsType>()
  const [collectionId, setCollectionId] = useState<{
    id: string | null
    name: string | null
  }>()

  const [currentProduct, setCurrentProduct] = useState(product)
  const [renderVivaraYou, setRenderVivaraYou] = useState<boolean>(false)

  const shelfClass = shelfClassVariant(variant)

  const {
    id,
    name,
    sku,
    brand,
    isVariantOf,
    gtin,
    releaseDate,
    image: productImages,
    offers: { lowPrice: spotPrice, offers },
    additionalProperty,
    breadcrumbList,
    slug
  } = currentProduct

  const { hasVariant } = isVariantOf

  const skuPriceRange = useMemo(() => {
    const { breadcrumbList } = currentProduct

    const validatedConditionsPriceRange: boolean =
      priceRangeConditions(breadcrumbList)

    if (!validatedConditionsPriceRange) {
      return null
    }

    if (validatedConditionsPriceRange) {
      return getSKUPriceRange(hasVariant)
    }

    return null
  }, [hasVariant, currentProduct])

  const isNewRelease = useReleaseDate(releaseDate)
  const { hasVariant: variants } = isVariantOf
  const haveVariants = variants && variants.length > 1
  const [img] = productImages
  const [cardImage, setCardImage] = useState<string>(img.url)
  const newVariantsInStock = variantInStock(
    variants,
    gtin?.indexOf('KIT') >= 0
  ).sort((a, b) => {
    const valueA =
      typeof a?.additionalProperty[0]?.value === 'number'
        ? a?.additionalProperty[0]?.value
        : parseFloat(a?.additionalProperty[0]?.value)

    const valueB =
      typeof b?.additionalProperty[0]?.value === 'number'
        ? b?.additionalProperty[0]?.value
        : parseFloat(b?.additionalProperty[0]?.value)

    return valueA - valueB
  })

  useEffect(() => {
    addProductOnReducer(productsDataToEvent, inView, currentProduct, dispatcher)

    if (inView && setPayloadEvent) {
      setPayloadEvent((prevState) => [...prevState, currentProduct])
    }
  }, [productsDataToEvent, inView, currentProduct, dispatcher, setPayloadEvent])

  useEffect(() => {
    if (currentProduct) {
      setCollections(currentProduct?.clusterHighlights)
      setAllCollections(currentProduct?.productClusters)
      setCardImage(OrderImagesByRegex(currentProduct?.image)?.[0]?.url)
    }
  }, [currentProduct])

  const selectOffer = offers?.findIndex(
    (prev) => spotPrice === prev?.price || spotPrice === 0
  )

  const listPrice = offers[selectOffer]?.listPrice ?? 0
  const seller = offers[selectOffer]?.seller ?? { identifier: '1' }
  const price = offers[selectOffer]?.price ?? 0

  let linkProps = useProductLink({ index, product: currentProduct })

  if (!offers[selectOffer]) {
    linkProps = {
      to: `/404/?${linkProps?.to?.split('/')[1]}`,
      onClick: linkProps?.onClick,
      'data-testid': 'product-link',
    }
  }

  const { width } = useWindowDimensions()
  const isNotebook = width > 965
  const isMobile = width < 1279
  const buyProps = useBuyButton({
    id,
    price,
    listPrice,
    seller,
    quantity: 1,
    itemOffered: {
      sku,
      name,
      gtin,
      image: productImages,
      brand,
      isVariantOf,
      additionalProperty,
    },
    breadcrumbList,
    slug: slug
  })

  TagsCollection({ collections, setCollectionId })

  const wishlistProduct = [
    {
      product: newVariantsInStock?.[0],
      chosenOption: 0,
      productSize:
        newVariantsInStock?.[0]?.additionalProperty?.[0]?.value?.toString(),
    },
  ]

  const isKit = gtin?.indexOf('KIT') >= 0
  const KitOrPDPObject = {
    product,
    isKit,
    linkProps,
    name,
    cardImage,
    img,
    aspectRatio,
    shelfClass,
    isRelatedProducts,
  }

  const newListPrice =
    newVariantsInStock?.[0]?.offers?.offers?.[0]?.listPrice ?? 0

  const newSpotPrice = newSpotPriceProduct(
    isKit,
    newVariantsInStock,
    newListPrice
  )

  const newPrice = newVariantsInStock?.[0]?.offers?.offers?.[0]?.price ?? 0
  const newIsProductAvailable = isProductAvailableInStock(
    isKit,
    newVariantsInStock,
    newPrice
  )

  const buyDisabled = !newIsProductAvailable

  const hasPolarizedLens = product?.isVariantOf?.additionalProperty.some(
    (property) => property.name === 'polarized_lens'
  )

  useEffect(() => {
    if (collectionId?.id === '2295') {
      validateVivaraYou(
        product?.isVariantOf?.productGroupID,
        setRenderVivaraYou
      )
    }
  }, [collectionId])

  return (
    <>
      <HandleModalSaveProductAndWishlist product={currentProduct} />
      <ProductCardReturn
        shelfClass={shelfClass}
        productImages={OrderImagesByRegex(currentProduct?.image)}
        setIsMouseOver={setIsMouseOver}
        setCardImage={setCardImage}
        cardRef={ref}
        KitOrPDPObject={KitOrPDPObject}
        product={currentProduct}
        index={index}
        wishlistProduct={wishlistProduct}
        linkProps={linkProps}
        wishlistStyle={wishlistStyle}
        newVariantsInStock={newVariantsInStock}
        collections={collections}
        allCollections={allCollections}
        collectionId={collectionId}
        isNewRelease={isNewRelease}
        isKit={isKit}
        isNotebook={isNotebook}
        newSpotPrice={newSpotPrice}
        newListPrice={newListPrice}
        newPrice={newPrice}
        newIsProductAvailable={newIsProductAvailable}
        haveVariants={haveVariants}
        variants={variants}
        buyDisabled={buyDisabled}
        buyProps={buyProps}
        hasPolarizedLens={hasPolarizedLens}
        isMouseOver={isMouseOver}
        name={name}
        setCurrentProduct={setCurrentProduct}
        isMobile={isMobile}
        renderVivaraYou={renderVivaraYou}
        border={border}
        inView={inView}
        skuPriceRange={skuPriceRange}
        isRelatedProducts={isRelatedProducts}
      />
    </>
  )
}

export default memo(ProductCard)
